import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BUILDER } from "@/constants/testIds";

const DEFAULT_STATE = {
  name: "",
  gender: "",
  birth_year: "",
  death_year: "",
  birth_place: "",
  portrait_url: "",
  notes: "",
  is_you: false,
};

function toState(person) {
  if (!person) return { ...DEFAULT_STATE };
  return {
    name: person.name || "",
    gender: person.gender || "",
    birth_year: person.birth_year ?? "",
    death_year: person.death_year ?? "",
    birth_place: person.birth_place || "",
    portrait_url: person.portrait_url || "",
    notes: person.notes || "",
    is_you: !!person.is_you,
  };
}

const KIND_TITLES = {
  parent: "Add a parent",
  spouse: "Add a spouse",
  child: "Add a child",
};

export default function PersonDialog({
  open, mode, initial, linkContext, onClose, onSubmit, onDelete,
}) {
  const [state, setState] = useState(DEFAULT_STATE);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setState(toState(initial));
  }, [open, initial]);

  const set = (k) => (e) => setState((s) => ({ ...s, [k]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!state.name.trim()) return;
    setBusy(true);
    try {
      await onSubmit({
        name: state.name.trim(),
        gender: state.gender || null,
        birth_year: state.birth_year ? Number(state.birth_year) : null,
        death_year: state.death_year ? Number(state.death_year) : null,
        birth_place: state.birth_place.trim() || null,
        portrait_url: state.portrait_url.trim() || null,
        notes: state.notes.trim() || null,
        is_you: !!state.is_you,
      });
    } finally {
      setBusy(false);
    }
  }

  const title = linkContext
    ? `${KIND_TITLES[linkContext.kind] || "Add person"} — for ${linkContext.anchorName}`
    : mode === "edit" ? "Edit person" : "Add a person";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid={BUILDER.personDialog}
        className="bg-ivory border-ivory-300 max-w-lg p-0 overflow-hidden"
      >
        <div className="p-7">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight text-ink">
              {title}
            </DialogTitle>
            <DialogDescription className="text-ink-50 mt-1">
              Only the name is required. You can always add more later.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <Field label="Name">
              <Input
                data-testid={BUILDER.personName}
                value={state.name}
                onChange={set("name")}
                required
                autoFocus
                placeholder="e.g. Ravi Menon"
                className="h-11 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Gender">
                <select
                  data-testid={BUILDER.personGender}
                  value={state.gender}
                  onChange={set("gender")}
                  className="h-11 w-full rounded-md border border-ivory-300 bg-transparent px-3 text-sm focus:outline-none focus:ring-2 focus:ring-tulsi"
                >
                  <option value="">—</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </Field>
              <Field label="Home / Village">
                <Input
                  data-testid={BUILDER.personPlace}
                  value={state.birth_place}
                  onChange={set("birth_place")}
                  placeholder="Palakkad, Kerala"
                  className="h-11 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Born">
                <Input
                  data-testid={BUILDER.personBirth}
                  type="number"
                  min={1}
                  max={2100}
                  value={state.birth_year}
                  onChange={set("birth_year")}
                  placeholder="1948"
                  className="h-11 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
                />
              </Field>
              <Field label="Died (optional)">
                <Input
                  data-testid={BUILDER.personDeath}
                  type="number"
                  min={1}
                  max={2100}
                  value={state.death_year}
                  onChange={set("death_year")}
                  placeholder="—"
                  className="h-11 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
                />
              </Field>
            </div>

            <Field label="Portrait URL (optional)">
              <Input
                data-testid={BUILDER.personPortrait}
                value={state.portrait_url}
                onChange={set("portrait_url")}
                placeholder="https://…"
                className="h-11 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
              />
            </Field>

            <Field label="A short story">
              <Textarea
                data-testid={BUILDER.personNotes}
                value={state.notes}
                onChange={set("notes")}
                rows={3}
                placeholder="What should this person be remembered for?"
                className="bg-transparent border-ivory-300 focus-visible:ring-tulsi"
              />
            </Field>

            <label className="flex items-center gap-2 text-sm text-ink-50">
              <input
                data-testid={BUILDER.personIsYou}
                type="checkbox"
                checked={state.is_you}
                onChange={(e) => setState((s) => ({ ...s, is_you: e.target.checked }))}
                className="accent-saffron"
              />
              This person is me
            </label>

            <div className="flex gap-2 pt-2">
              {onDelete && (
                <button
                  type="button"
                  data-testid={BUILDER.personDelete}
                  onClick={onDelete}
                  className="h-11 px-4 rounded-full text-sm text-rust border border-rust/40 hover:bg-rust/5 transition"
                >
                  Remove
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="ml-auto h-11 px-4 rounded-full text-sm text-ink-50 hover:text-ink transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid={BUILDER.personSave}
                disabled={busy || !state.name.trim()}
                className="h-11 rounded-full bg-saffron text-ivory px-6 text-sm font-medium hover:bg-saffron-deep disabled:opacity-60 transition"
              >
                {busy ? "Saving…" : mode === "edit" ? "Save changes" : "Add to tree"}
              </button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-ink-50">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
